import { useEffect, useMemo, useState } from 'react';
import { onValue, ref, query, orderByChild, startAt } from 'firebase/database';
import { rtdb } from '../firebase';

/**
 * Single subscription that feeds ALL crous with their live pulse.
 *
 * Returns a map { crousId -> { level, count, lastTs, trend } } where:
 *   level: 0..2 weighted average over the last 15 min, or null if no pings
 *   count: number of fresh pings within the window
 *   lastTs: timestamp of the most recent ping
 *   trend: 'up' | 'down' | 'flat' | 'new' — computed vs. the previous 15 min
 */

const WINDOW_MS = 15 * 60 * 1000;
const PREV_WINDOW_MS = 15 * 60 * 1000;

function weightedLevel(pings, now, from, to) {
  let num = 0;
  let den = 0;
  for (const p of pings) {
    if (p.timestamp < from || p.timestamp >= to) continue;
    const age = now - p.timestamp;
    const fresh = Math.max(0.1, 1 - age / WINDOW_MS);
    const w = fresh * (typeof p.trustWeight === 'number' ? p.trustWeight : 1);
    num += p.level * w;
    den += w;
  }
  return { level: den > 0 ? num / den : null, count: pings.filter(p => p.timestamp >= from && p.timestamp < to).length };
}

export default function useAllCrousLive() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = Date.now();
    const since = now - (WINDOW_MS + PREV_WINDOW_MS);
    const q = query(ref(rtdb, 'pings'), orderByChild('timestamp'), startAt(since));
    const unsub = onValue(q, (snap) => {
      const rows = [];
      snap.forEach((c) => {
        const v = c.val();
        if (v && typeof v.level === 'number' && typeof v.timestamp === 'number' && v.crousId) {
          rows.push(v);
        }
      });
      setRaw(rows);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const pulse = useMemo(() => {
    const now = Date.now();
    const byCrous = new Map();
    for (const p of raw) {
      if (!byCrous.has(p.crousId)) byCrous.set(p.crousId, []);
      byCrous.get(p.crousId).push(p);
    }
    const out = {};
    for (const [crousId, list] of byCrous.entries()) {
      const current = weightedLevel(list, now, now - WINDOW_MS, now);
      const previous = weightedLevel(list, now, now - (WINDOW_MS + PREV_WINDOW_MS), now - WINDOW_MS);
      let trend = 'flat';
      if (previous.level === null && current.level !== null) trend = 'new';
      else if (previous.level !== null && current.level !== null) {
        const delta = current.level - previous.level;
        if (delta > 0.2) trend = 'up';
        else if (delta < -0.2) trend = 'down';
      }
      const lastTs = list.reduce((m, p) => (p.timestamp > m ? p.timestamp : m), 0);
      out[crousId] = {
        level: current.level,
        count: current.count,
        lastTs,
        trend
      };
    }
    return out;
  }, [raw]);

  return { pulse, loading };
}
